import React, { useCallback, useEffect, useState } from 'react';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  setIsTaskModalOpen,
  setSelectedTaskTableRow,
  setTaskTableSettings,
  type TaskTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { deduplicateArray } from 'src/utils/helpers';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

/**
 * Default component
 */
const TaskTable = () => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TaskTableDataRow>('label');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const { taskTableData, taskSearchFieldValue, taskTableSettings } = useAppSelector((state) => state.taskConfigPage);
  const [sortedTableData, setSortedTableData] = useState(taskTableData);
  const { currentPage, perPage } = taskTableSettings;

  // Configure "onSort" function
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof TaskTableDataRow; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );

  // Filter, sorting, and pagination
  useEffect(() => {
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      taskTableData,
      taskSearchFieldValue,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(paginatedData);
    dispatch(setTaskTableSettings({ totalPages }));
  }, [sortColumn, sortDirection, taskTableData, taskSearchFieldValue, currentPage, perPage]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setTaskTableSettings({ currentPage: 1 }));
  }, [taskSearchFieldValue, sortColumn, sortDirection]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        {/* Header */}
        <TableRow>
          <TableCell width="20%" sortColumn="label">
            Task Name
          </TableCell>
          <TableCell sortColumn="regions">State/Region/Province</TableCell>
          <TableCell sortColumn="countries">Country</TableCell>
          <TableCell sortColumn="identifyBodyLocation">Identify Body Location?</TableCell>
          <TableCell sortColumn="isMeasuredBy">Is Measured By?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>

        {/* Rows */}
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id}>
            <TableCell>{row.label}</TableCell>
            <TableCell>{row.regions.length === 1 ? `${row.regions[0]}` : `${row.regions.length} regions`}</TableCell>
            <TableCell>
              {deduplicateArray(row.countries).length == 1
                ? row.countries[0]
                : `${deduplicateArray(row.countries).length} countries`}
            </TableCell>
            {row.regions.length > 1 &&
              row.regions.map((_, index) => (
                <TableRow key={index}>
                  <TableCell></TableCell>
                  <TableCell>{row.regions[index]}</TableCell>
                  <TableCell>{row.countries[index]}</TableCell>
                </TableRow>
              ))}
            <TableCell>{row.identifyBodyLocation ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.isMeasuredBy}</TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>
              <div
                className="view-and-edit-tasks__edit-icon"
                onClick={() => {
                  dispatch(setIsTaskModalOpen(true));
                  dispatch(setSelectedTaskTableRow(row));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </div>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

// **** Export **** //

export default TaskTable;
